// Nordic Secure DFU (nRF5 SDK 17.1) — the protocol nRF Connect speaks to the
// wearable's bootloader, reimplemented so Wearable Ops can update firmware
// itself. Transport-agnostic: everything here talks to a small `transport`
// object ({ writeControl, writePacket, onControlNotify, onDisconnect,
// isConnected }), so it runs against Web Bluetooth in the app and against a
// simulated bootloader in tests. The Web Bluetooth glue is in bleDfu.js.
//
// Behaviour below was checked against the SDK source the bootloader is built
// from (wearable-firmware/SDK/nRF5_SDK_17.1.0_ddde560):
//   - Creating the command (init) object resets ALL progress, so every run
//     starts the firmware from offset 0 — no resume bookkeeping needed.
//   - Re-creating a data object rewinds to the last executed object, so a bad
//     object can simply be sent again.
//   - The CRC the bootloader reports for data is cumulative over the image.
//   - The packet receipt counter resets on every Create.

import { unzipSync, strFromU8, strToU8 } from "fflate";
import { normalizeRelease } from "./firmwareRelease";

export const DFU_SERVICE = 0xfe59;
export const DFU_CONTROL_POINT = "8ec90001-f315-4f60-9fb8-838830daea50";
export const DFU_PACKET = "8ec90002-f315-4f60-9fb8-838830daea50";
// Unbonded buttonless characteristic, exposed by the running app firmware.
export const DFU_BUTTONLESS = "8ec90003-f315-4f60-9fb8-838830daea50";

const OP = {
  CREATE: 0x01,
  SET_PRN: 0x02,
  CALC_CRC: 0x03,
  EXECUTE: 0x04,
  SELECT: 0x06,
  RESPONSE: 0x60,
};
const OBJ_COMMAND = 0x01;
const OBJ_DATA = 0x02;
const RES_SUCCESS = 0x01;
const RES_EXTENDED_ERROR = 0x0b;

const BUTTONLESS_OP = { ENTER_BOOTLOADER: 0x01, SET_ADV_NAME: 0x02 };
const BUTTONLESS_RESPONSE = 0x20;
const ADV_NAME_MAX = 20;

// Nordic's names for the Control Point result / extended error codes. The UI
// maps the interesting ones to operator-facing copy; the rest show the name.
export const DFU_RESULT_NAMES = {
  0x00: "INVALID",
  0x02: "OP_CODE_NOT_SUPPORTED",
  0x03: "INVALID_PARAMETER",
  0x04: "INSUFFICIENT_RESOURCES",
  0x05: "INVALID_OBJECT",
  0x07: "UNSUPPORTED_TYPE",
  0x08: "OPERATION_NOT_PERMITTED",
  0x0a: "OPERATION_FAILED",
  0x0b: "EXTENDED_ERROR",
};
export const DFU_EXT_ERROR_NAMES = {
  0x01: "INVALID_ERROR_CODE",
  0x02: "WRONG_COMMAND_FORMAT",
  0x03: "UNKNOWN_COMMAND",
  0x04: "INIT_COMMAND_INVALID",
  0x05: "FW_VERSION_FAILURE",
  0x06: "HW_VERSION_FAILURE",
  0x07: "SD_VERSION_FAILURE",
  0x08: "SIGNATURE_MISSING",
  0x09: "WRONG_HASH_TYPE",
  0x0a: "HASH_FAILED",
  0x0b: "WRONG_SIGNATURE_TYPE",
  0x0c: "VERIFICATION_FAILED",
  0x0d: "INSUFFICIENT_SPACE",
};

/**
 * Every failure carries a `code` the UI can switch on:
 *   package    — the zip isn't a usable DFU package
 *   rejected   — the bootloader answered with an error (`result`, `extended`)
 *   disconnected, timeout, corrupt (CRC kept mismatching), buttonless
 */
export class DfuError extends Error {
  constructor(code, message, extra = {}) {
    super(message);
    this.name = "DfuError";
    this.code = code;
    Object.assign(this, extra);
  }
}

// ── CRC-32 (zlib / IEEE 802.3) — what the bootloader reports ──────────────
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

/** CRC-32 of `bytes`, continuing from `crc` (pass a previous result). */
export const crc32 = (bytes, crc = 0) => {
  let c = ~crc >>> 0;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return ~c >>> 0;
};

// ── Package ──────────────────────────────────────────────────────────────

const IMAGE_TYPES = [
  "application",
  "bootloader",
  "softdevice",
  "softdevice_bootloader",
];

/**
 * Unpack an nrfutil DFU zip ({ manifest.json, <x>.dat, <x>.bin }). Only
 * single-image packages are supported — that is what our OTA build produces
 * (application only). A multi-image package needs a second bootloader session
 * after a reboot, which we don't drive.
 * An optional release.json at the root (added by wearable-firmware's
 * package_ota.sh) carries the version and release notes; Nordic's tools
 * ignore it. A malformed one is ignored here too.
 * @returns {{ type, initPacket: Uint8Array, firmware: Uint8Array, release }}
 */
export const parseDfuPackage = (zipBytes) => {
  let files;
  try {
    files = unzipSync(new Uint8Array(zipBytes));
  } catch (e) {
    throw new DfuError("package", "Not a zip file");
  }
  if (!files["manifest.json"]) {
    throw new DfuError("package", "manifest.json missing");
  }
  let manifest;
  try {
    manifest = JSON.parse(strFromU8(files["manifest.json"])).manifest;
  } catch (e) {
    throw new DfuError("package", "manifest.json is not valid JSON");
  }
  const types = IMAGE_TYPES.filter((type) => manifest?.[type]);
  if (types.length === 0) {
    throw new DfuError("package", "manifest lists no firmware image");
  }
  if (types.length > 1) {
    throw new DfuError("package", "multi-image packages are not supported", {
      multiImage: true,
    });
  }
  const [type] = types;
  const { bin_file: binFile, dat_file: datFile } = manifest[type];
  const firmware = files[binFile];
  const initPacket = files[datFile];
  if (!firmware?.length || !initPacket?.length) {
    throw new DfuError("package", `${binFile} or ${datFile} missing`);
  }
  let release = null;
  if (files["release.json"]) {
    try {
      release = normalizeRelease(JSON.parse(strFromU8(files["release.json"])));
    } catch (e) {
      release = null;
    }
  }
  return { type, initPacket, firmware, release };
};

// ── Control Point ────────────────────────────────────────────────────────

const u32 = (n) => [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, n >>> 24];

const rejection = (opcode, result, extended) =>
  new DfuError(
    "rejected",
    `Bootloader rejected op 0x${opcode.toString(16)}: ` +
      (result === RES_EXTENDED_ERROR
        ? DFU_EXT_ERROR_NAMES[extended] || `ext 0x${extended?.toString(16)}`
        : DFU_RESULT_NAMES[result] || `0x${result.toString(16)}`),
    {
      opcode,
      result,
      extended: result === RES_EXTENDED_ERROR ? extended : undefined,
    }
  );

/**
 * Request/response over the Control Point. Responses are matched by opcode;
 * Packet Receipt Notifications arrive as unsolicited CALC_CRC responses and
 * are matched the same way.
 */
class ControlPoint {
  constructor(transport) {
    this.transport = transport;
    this.waiters = [];
    this.unsubscribe = [
      transport.onControlNotify((bytes) => this.handle(bytes)),
      transport.onDisconnect?.(() =>
        this.failAll(new DfuError("disconnected", "Device disconnected"))
      ),
    ];
  }

  handle(bytes) {
    if (bytes[0] !== OP.RESPONSE) return;
    const index = this.waiters.findIndex((w) => w.opcode === bytes[1]);
    if (index === -1) return;
    const [waiter] = this.waiters.splice(index, 1);
    clearTimeout(waiter.timer);
    if (bytes[2] === RES_SUCCESS) {
      waiter.resolve(
        new DataView(bytes.buffer, bytes.byteOffset + 3, bytes.length - 3)
      );
    } else {
      waiter.reject(rejection(bytes[1], bytes[2], bytes[3]));
    }
  }

  // Register before the write that triggers the response, so a fast reply
  // can't slip past.
  wait(opcode, timeoutMs) {
    let waiter;
    const promise = new Promise((resolve, reject) => {
      waiter = { opcode, resolve, reject };
      waiter.timer = setTimeout(() => {
        this.waiters = this.waiters.filter((w) => w !== waiter);
        reject(
          new DfuError(
            "timeout",
            `No response to op 0x${opcode.toString(16)} within ${timeoutMs} ms`
          )
        );
      }, timeoutMs);
      this.waiters.push(waiter);
    });
    promise.cancel = () => {
      clearTimeout(waiter.timer);
      this.waiters = this.waiters.filter((w) => w !== waiter);
    };
    return promise;
  }

  // The response timeout also covers a write that never completes.
  async request(bytes, timeoutMs = 10000) {
    const response = this.wait(bytes[0], timeoutMs);
    const write = this.transport
      .writeControl(Uint8Array.from(bytes))
      .catch((e) => {
        response.cancel();
        throw this.transport.isConnected()
          ? e
          : new DfuError("disconnected", "Device disconnected");
      });
    const [, view] = await Promise.all([write, response]);
    return view;
  }

  failAll(error) {
    const waiters = this.waiters;
    this.waiters = [];
    waiters.forEach((w) => {
      clearTimeout(w.timer);
      w.reject(error);
    });
  }

  dispose() {
    this.unsubscribe.forEach((fn) => fn?.());
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const readOffsetCrc = (view) => ({
  offset: view.getUint32(0, true),
  crc: view.getUint32(4, true),
});

// Web Bluetooth errors that mean this connection is no use any more (macOS
// handed over services that have since gone, or the device is out of reach).
export const isStaleLink = (e) =>
  /no longer exists|no longer in range|disconnected/i.test(e?.message || "");

// Signals "these bytes didn't arrive intact" — retry the object, maybe with
// smaller packets. Never escapes runSecureDfu.
class TransferGlitch extends Error {}

/**
 * Send a firmware package to a device that is already in the bootloader.
 *
 * Packet size: Web Bluetooth can't tell us the negotiated MTU, so we start
 * with the largest write the bootloader accepts (MTU 247 → 244 bytes) and step
 * down whenever an object fails its offset/CRC check or a write is refused.
 * 20 bytes always fits.
 *
 * @param {object} opts
 * @param {object} opts.transport
 * @param {Uint8Array} opts.initPacket
 * @param {Uint8Array} opts.firmware
 * @param {(p: {sent: number, total: number}) => void} [opts.onProgress]
 * @param {number} [opts.prn] Packet Receipt Notification interval (0 = off)
 * @param {number} [opts.prepareMs] pause after creating the first data object,
 *   and after re-creating one that failed, before streaming into it
 */
export const runSecureDfu = async ({
  transport,
  initPacket,
  firmware,
  onProgress,
  prn = 12,
  packetSizes = [244, 182, 20],
  prnTimeoutMs = 5000,
  executeTimeoutMs = 20000,
  settleMs = 100,
  settleTries = 5,
  prepareMs = 1000,
  log = () => {},
}) => {
  const cp = new ControlPoint(transport);
  let sizeIndex = 0;

  const select = async (type) => {
    const view = await cp.request([OP.SELECT, type]);
    return {
      maxSize: view.getUint32(0, true),
      ...readOffsetCrc(new DataView(view.buffer, view.byteOffset + 4)),
    };
  };

  // Stream bytes [start, end) of `buf`; with PRN on, every prn-th packet must
  // be acknowledged with the offset/CRC we expect so far.
  const stream = async (buf, start, end) => {
    const packetSize = packetSizes[sizeIndex];
    let count = 0;
    for (let at = start; at < end; at += packetSize) {
      const stop = Math.min(at + packetSize, end);
      count += 1;
      const receipt =
        prn && count % prn === 0 ? cp.wait(OP.CALC_CRC, prnTimeoutMs) : null;
      receipt?.catch(() => {}); // awaited below; don't report it unhandled meanwhile
      try {
        await transport.writePacket(buf.subarray(at, stop));
      } catch (e) {
        receipt?.cancel();
        // macOS sometimes hands over a stale copy of the services, or has
        // already lost the link without saying so: no retry on this
        // connection can help, so reconnect.
        if (!transport.isConnected() || isStaleLink(e)) {
          throw new DfuError(
            "disconnected",
            `Device disconnected (${e.message})`
          );
        }
        throw new TransferGlitch(`write refused: ${e.message}`);
      }
      if (receipt) {
        let ack;
        try {
          ack = readOffsetCrc(await receipt);
        } catch (e) {
          if (e.code === "timeout") throw new TransferGlitch("receipt missing");
          throw e;
        }
        if (ack.offset !== stop || ack.crc !== crc32(buf.subarray(0, stop))) {
          throw new TransferGlitch(
            `receipt at ${ack.offset}, expected ${stop}`
          );
        }
      }
    }
  };

  // The bootloader's offset/CRC once the object's packets have landed. On
  // macOS a write with response (this request) can overtake packets still
  // queued as writes without response, so the bootloader may answer before
  // the last few arrive ("object ended at 3172, expected 4096" on real units,
  // with the rest arriving just after). Ask again briefly before calling it a
  // glitch.
  const settledOffsetCrc = async (end) => {
    let res = readOffsetCrc(await cp.request([OP.CALC_CRC]));
    for (let i = 0; res.offset < end && i < settleTries; i++) {
      await sleep(settleMs);
      res = readOffsetCrc(await cp.request([OP.CALC_CRC]));
    }
    return res;
  };

  // Create → stream → verify CRC → execute one object, retrying as needed.
  // A bootloader that has just accepted the init packet is still busy for a
  // moment (settings and flash work) and drops data packets meanwhile: on
  // real units the first object lost every packet after the first receipt
  // ("object ended at 3172, expected 4096"), then the link died. So the
  // first data object, and any re-created after a failure, waits first, as
  // Nordic's own DFU libraries do.
  const sendObject = async (type, buf, start, end, executeTimeout) => {
    let attemptsAtSmallest = 0;
    let failed = false;
    for (;;) {
      await cp.request([OP.CREATE, type, ...u32(end - start)]);
      if (type === OBJ_DATA && (start === 0 || failed) && prepareMs > 0) {
        await sleep(prepareMs);
      }
      try {
        await stream(buf, start, end);
        const { offset, crc } = await settledOffsetCrc(end);
        if (offset !== end || crc !== crc32(buf.subarray(0, end))) {
          throw new TransferGlitch(
            `object ended at ${offset}, expected ${end}`
          );
        }
      } catch (e) {
        if (!(e instanceof TransferGlitch)) throw e;
        failed = true;
        log(
          `object at ${start} failed (${e.message}) with ${packetSizes[sizeIndex]} B packets`
        );
        if (sizeIndex < packetSizes.length - 1) {
          sizeIndex += 1;
        } else if (++attemptsAtSmallest >= 3) {
          throw new DfuError("corrupt", `Transfer kept failing: ${e.message}`);
        }
        // Let stragglers from the failed attempt land before Create rewinds
        // the object, so they can't end up in the retry.
        await sleep(settleMs * 3);
        continue;
      }
      await cp.request([OP.EXECUTE], executeTimeout);
      return;
    }
  };

  try {
    await cp.request([OP.SET_PRN, prn & 0xff, (prn >> 8) & 0xff]);

    const command = await select(OBJ_COMMAND);
    log(`init object: max ${command.maxSize} B`);
    if (initPacket.length > command.maxSize) {
      throw new DfuError(
        "package",
        "Init packet larger than bootloader allows"
      );
    }
    // The init packet is where the bootloader checks the signature, hardware
    // and SoftDevice requirements — a wrong package is rejected right here.
    await sendObject(OBJ_COMMAND, initPacket, 0, initPacket.length);

    log("init packet accepted (signature OK)");
    const data = await select(OBJ_DATA);
    log(`data objects: ${data.maxSize} B, packets ${packetSizes[sizeIndex]} B`);
    const objectSize = data.maxSize || 4096;
    onProgress?.({ sent: 0, total: firmware.length });
    for (let start = 0; start < firmware.length; start += objectSize) {
      const end = Math.min(start + objectSize, firmware.length);
      const isLast = end === firmware.length;
      // The last Execute makes the bootloader hash-check the whole image
      // before answering, so it gets a longer timeout.
      await sendObject(
        OBJ_DATA,
        firmware,
        start,
        end,
        isLast ? executeTimeoutMs : undefined
      );
      onProgress?.({ sent: end, total: firmware.length });
    }
  } finally {
    cp.dispose();
  }
};

// ── Buttonless (app firmware → bootloader) ───────────────────────────────

/**
 * Ask the running app to reboot into the bootloader. If `advName` is given the
 * bootloader advertises under that name instead of its default, so we can pick
 * out this exact unit even with other wearables in update mode nearby.
 * Resolves once the device has acknowledged — it resets right after.
 *
 * @param {object} buttonless { write(bytes), onIndicate(handler) → unsubscribe }
 */
export const enterBootloader = async (
  buttonless,
  { advName, timeoutMs = 8000 } = {}
) => {
  const pending = new Map();
  const unsubscribe = buttonless.onIndicate((bytes) => {
    if (bytes[0] !== BUTTONLESS_RESPONSE) return;
    const waiter = pending.get(bytes[1]);
    if (!waiter) return;
    pending.delete(bytes[1]);
    if (bytes[2] === RES_SUCCESS) waiter.resolve();
    else
      waiter.reject(
        new DfuError(
          "buttonless",
          `Device refused op 0x${bytes[1].toString(16)} (0x${bytes[2].toString(
            16
          )})`
        )
      );
  });
  const request = (bytes) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pending.delete(bytes[0]);
        reject(new DfuError("timeout", "Device didn't answer the DFU request"));
      }, timeoutMs);
      pending.set(bytes[0], {
        resolve: () => {
          clearTimeout(timer);
          resolve();
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      });
      Promise.resolve(buttonless.write(Uint8Array.from(bytes))).catch((e) => {
        clearTimeout(timer);
        pending.delete(bytes[0]);
        reject(new DfuError("buttonless", e.message));
      });
    });

  try {
    if (advName) {
      const name = Array.from(strToU8(advName)).slice(0, ADV_NAME_MAX);
      await request([BUTTONLESS_OP.SET_ADV_NAME, name.length, ...name]);
    }
    await request([BUTTONLESS_OP.ENTER_BOOTLOADER]);
  } finally {
    unsubscribe();
  }
};
